import { AsyncStatus } from 'consts'
import { SortDirections } from 'consts/filterConsts'
import { GI_RESULT_MSG_TYPE } from 'consts/giConsts'
import { find, matchesProperty } from 'lodash'
import moment from 'moment'
import { logInfo } from 'utils/logUtils'
import { mapToPromotions } from 'utils/mappers/messagesMapper'
import Actions from '../actions'
import { removeWhere, replaceItem, updateAllWhere, updateWhere, upsertItem } from '../utils/arrayUtils'
import {
  bringRoomToTop,
  generateDummyRoom,
  handleUploadingLocalMessages,
  insertLocalMessages,
  updateRoomLastMessage,
  upsertMessagesList
} from '../utils/chatUtils'
import { mergeReceivedRooms } from '../utils/roomsUtils'
import { removePatientTagFromPatient, upsertPatientTag } from '../utils/tagUtils'

const initialState = {
  messages: {
    items: [],
    isLoading: false
  },
  scheduledMessages: {
    items: [],
    wasLastSentMessageScheduled: false
  },
  activeRoomId: null,
  pinnedPatient: null,
  rooms: [],
  roomsLoadedCount: 0,
  lastRoomsRequestTime: null,
  sort: {
    direction: 'desc'
  },
  filter: {
    tagsFilter: {},
    searchFilter: null,
    doctorFilter: '',
    hiTrackedOnlyFilter: null
  },
  pagination: {
    hasMore: false,
    nextToken: null
  },
  shouldAutoScrollChat: true,
  overlayLoader: true,
  shouldRequestRooms: true,
  uploadingMessages: [],
  promotions: {
    data: [],
    lastMessages: {
      roomId: null,
      data: []
    },
    isFetching: false,
    fetchError: false,
    isSaving: false
  },
  isLoading: false,
  broadcast: {
    isModeOn: false,
    selectedPatients: [],
    excluded: [],
    allSelected: false,
    isLoading: false,
    isConfirmationModalOpen: false,
    localMessages: [],
    isPersonalMessage: false
  },
  messageActions: {
    editMessage: {
      loadStatus: null
    },
    viewHistory: {
      loadStatus: null,
      message: {}
    }
  }
}

export default (state = initialState, action) => {
  switch (action.type) {
    case Actions.SIGNOUT_RECEIVED:
      return initialState
    case Actions.PATIENT_REQUESTED:
      return {
        ...state,
        messages: initialState.messages
      }
    case Actions.PATIENT_MESSAGES_REQUESTED:
      return {
        ...state,
        shouldAutoScrollChat: !action.payload.nextToken,
        activeRoomId: action.payload.roomId,
        messages: {
          ...state.messages,
          isLoading: true
        }
      }
    case Actions.SEARCH_RESULTS_RECEIVED:
      return {
        ...state
      }
    case Actions.PATIENT_MESSAGES_RECEIVED:
      return {
        ...state,
        messages: {
          ...state.messages,
          items:
            action.payload.method === 'push'
              ? [...state.messages.items, ...action.payload?.messages.items]
              : insertLocalMessages(action.payload?.messages.items, state.uploadingMessages, state.activeRoomId),
          nextToken: action.payload?.messages?.nextToken,
          isLoading: false
        },
        scheduledMessages: {
          ...state.scheduledMessages,
          items: [...action.payload?.scheduledMessages?.items.filter(scheduledMessage => !scheduledMessage._deleted)]
        }
      }
    case Actions.PATIENT_MESSAGES_FAILED:
      return {
        ...state,
        messages: {
          ...state.messages,
          isLoading: false
        }
      }
    case Actions.MESSAGE_ADDED: {
      let messages = state.messages.items
      if (action.payload.roomId === state.activeRoomId) {
        messages = upsertMessagesList(state.messages.items, action.payload)

        // If recevied a GI result message,
        // update the grinScan object in other messages which are associated to the same scan
        if (action.payload.type === GI_RESULT_MSG_TYPE) {
          messages = updateAllWhere(
            messages,
            msg => msg.entityRelatedId === action.payload.entityRelatedId,
            msg => ({ ...msg, grinScan: action.payload.grinScan })
          )
        }
      }

      return {
        ...state,
        messages: {
          ...state.messages,
          items: messages
        },
        rooms: bringRoomToTop(
          updateRoomLastMessage({ rooms: state.rooms, message: action.payload }),
          action.payload.roomId
        ),
        shouldAutoScrollChat: true,
        uploadingMessages: handleUploadingLocalMessages(state.uploadingMessages, action.payload)
      }
    }
    case Actions.SCHEDULED_MESSAGE_ADDED:
      return {
        ...state,
        scheduledMessages: {
          ...state.scheduledMessages,
          items:
            action.payload.roomId === state.activeRoomId && !action.payload.isSelf
              ? [...state.scheduledMessages.items, { ...action.payload, cache: action.payload.cache }]
              : state.scheduledMessages.items
        },
        rooms: bringRoomToTop(
          updateRoomLastMessage({ rooms: state.rooms, message: JSON.parse(action.payload.payload) }),
          action.payload.roomId
        ),
        shouldAutoScrollChat: true
      }
    case Actions.SCHEDULED_MESSAGE_SENT:
      return {
        ...state,
        scheduledMessages: {
          ...state.scheduledMessages,
          items: replaceItem(state.scheduledMessages.items, action.payload)
        }
      }
    case Actions.FETCH_ROOMS:
      return {
        ...state,
        isLoading: !action.payload?.withoutLoader || action.payload?.isLoading,
        isFetchingNext: !!action.payload?.withoutLoader,
        filter: {
          tagsFilter: action.payload?.tagsFilter || {},
          searchFilter: action.payload?.searchFilter || null,
          doctorFilter: action.payload?.doctorFilter || '',
          hiTrackedOnlyFilter:
            'hiTrackedOnlyFilter' in (action.payload || {})
              ? action.payload.hiTrackedOnlyFilter
              : state.filter.hiTrackedOnlyFilter
        },
        pagination: action.payload?.newSearch ? { ...initialState.pagination } : { ...state.pagination },
        roomsLoadedCount: action.payload?.newSearch ? 0 : state.roomsLoadedCount
      }
    case Actions.FETCH_SINGLE_ROOM_RECEIVED:
      return {
        ...state,
        rooms: [
          ...action.payload.items,
          ...state.rooms.filter(patientSM => !find(action.payload.items, matchesProperty('id', patientSM.id)))
        ]
      }
    case Actions.ROOMS_RECEIVED:
      if (state.lastRoomsRequestTime && moment(action.payload.requestTime).isBefore(state.lastRoomsRequestTime)) {
        logInfo(`ROOMS_RECEIVED: Ignoring outdated request at ${action.payload.requestTime}`)
        return state
      }

      const MAX_ROOMS_IN_MEMORY = 200

      let rooms = mergeReceivedRooms({
        stateRooms: state.rooms,
        items: action.payload.items,
        newSearch: action.payload.newSearch,
        addToTop: action.payload.addToTop
      })

      if (rooms.length > MAX_ROOMS_IN_MEMORY && (action.payload.addToTop || action.payload.newSearch)) {
        rooms = rooms.slice(0, MAX_ROOMS_IN_MEMORY)
      }

      const newItemsCount = action.payload.items?.length || 0
      const updatedRoomsLoadedCount = action.payload.newSearch
        ? newItemsCount
        : action.payload.addToTop
        ? state.roomsLoadedCount || 0
        : (state.roomsLoadedCount || 0) + newItemsCount
      const total = typeof action.payload.total === 'number' ? action.payload.total : null
      const pageLimit = action.payload.limit || 0
      const hasMore = action.payload.nextToken
        ? true
        : total !== null
        ? updatedRoomsLoadedCount < total
        : newItemsCount >= pageLimit && pageLimit > 0

      return {
        ...state,
        shouldRequestRooms: false,
        rooms,
        roomsLoadedCount: updatedRoomsLoadedCount,
        overlayLoader: false,
        isLoading: false,
        isFetchingNext: false,
        lastRoomsRequestTime: action.payload.requestTime,
        pagination: {
          nextToken: action.payload.nextToken || null,
          hasMore
        }
      }
    case Actions.FETCH_ROOMS_FAILED:
      return {
        ...state,
        isLoading: false,
        isFetchingNext: false
      }
    case Actions.MARK_ROOM_UNREAD:
      return {
        ...state,
        rooms: updateWhere(
          state.rooms,
          room => room.roomId === action.payload,
          room => (room.isChatRoomResolved = false)
        )
      }
    case Actions.UPDATE_ROOM_READ_STATUS:
      return {
        ...state,
        rooms: state.rooms.map(patientSM => ({
          ...patientSM,
          isChatRoomResolved: action.payload[patientSM?.id] ?? patientSM.isChatRoomResolved
        }))
      }
    case Actions.ADD_NEW_PATIENT_CHAT:
      return {
        ...state,
        rooms: [
          // `isOptimistic` lets a lagging post-invite search refresh (newSearch + addToTop)
          // keep this just-added row until the index returns it; dropped on the next
          // reconciling ROOMS_RECEIVED. See mergeReceivedRooms in utils/roomsUtils.
          { ...action.payload, isOptimistic: true },
          ...state.rooms.filter(room => room.patient.details.email !== action.payload.patient.details.email)
        ]
      }
    case Actions.UPDATE_ROOM:
      return {
        ...state,
        rooms: upsertItem({ array: state.rooms, item: action.payload, mergeOldProperties: true })
      }
    case Actions.INVITE_NEW_PATIENT:
      return {
        ...state,
        rooms: [generateDummyRoom(action.payload), ...state.rooms]
      }
    case Actions.INVITE_NEW_PATIENT_FAILED:
      return {
        ...state,
        rooms: state.rooms.filter(
          room => room.patient.details.email !== action.payload.patientEmail || room.isDummyRoom !== true
        )
      }
    case Actions.CHAT_ROOM_SELECTED:
    case Actions.TIMELINE_SELECTED:
      return {
        ...state,
        pinnedPatient: initialState.pinnedPatient,
        messages: initialState.messages,
        scheduledMessages: initialState.scheduledMessages,
        uploadingMessages: initialState.uploadingMessages
      }
    case Actions.PIN_CHAT_ROOM:
      return {
        ...state,
        pinnedPatient: action.payload
      }
    case Actions.UNPIN_CHAT_ROOM:
      return {
        ...state,
        pinnedPatient: null
      }
    case Actions.RESEND_PATIENT_INVITATION_REQUESTED:
    case Actions.DELETE_PATIENT_REQUESTED:
      return {
        ...state,
        ...(state.pinnedPatient?.id === action.payload.patientId && {
          pinnedPatient: { ...state.pinnedPatient, isLoading: true }
        }),
        rooms: updateWhere(
          state.rooms,
          room => room.patient.id === action.payload.patientId,
          room => (room.isLoading = true)
        )
      }
    case Actions.RESEND_PATIENT_INVITATION_RECEIVED:
      return {
        ...state,
        ...(state.pinnedPatient?.id === action.payload.patientId && {
          pinnedPatient: { ...state.pinnedPatient, isLoading: false }
        }),
        rooms: !action.payload.wasNewPatientCreated
          ? updateWhere(
              state.rooms,
              room => room.patient.id === action.payload.patientId,
              room => (room.isLoading = false)
            )
          : state.rooms.filter(room => room.patient.id !== action.payload.patientId)
      }
    case Actions.DELETE_PATIENT_RECEIVED:
      return {
        ...state,
        rooms: state.rooms.filter(room => room.patient.id !== action.payload.patientId)
      }
    case Actions.RESEND_PATIENT_INVITATION_FAILED:
    case Actions.DELETE_PATIENT_FAILED:
      return {
        ...state,
        ...(state.pinnedPatient?.id === action.payload.patientId && {
          pinnedPatient: { ...state.pinnedPatient, isLoading: false }
        }),
        rooms: updateWhere(
          state.rooms,
          room => room.patient.id === action.payload.patientId,
          room => (room.isLoading = false)
        )
      }
    case Actions.UPDATE_PATIENT_DETAILS_RECEIVED:
      return {
        ...state,
        rooms: updateWhere(
          state.rooms,
          room => room.patient.id === action.payload.patient.id,
          room => {
            room.name = action.payload.patient.details.name
            room.firstName = action.payload.patient.details.firstName
            room.lastName = action.payload.patient.details.lastName
            return (room.patient = { ...room.patient, ...action.payload.patient })
          }
        )
      }
    case Actions.ASSIGN_PATIENT_TO_PRACTICE_MEMBER:
      return {
        ...state,
        rooms: updateWhere(
          state.rooms,
          room => room.patientId === action.payload.patientId,
          room => {
            room.patient = upsertPatientTag(room.patient, action.payload)
            room.tagKeywords = [...room.tagKeywords, action.payload]
          }
        )
      }
    case Actions.ASSIGN_PATIENT_TAG:
      return {
        ...state,
        rooms: updateWhere(
          state.rooms,
          room => room.patientId === action.payload.patientId,
          room => (room.tagKeywords = [...room.tagKeywords, action.payload])
        )
      }
    case Actions.REMOVE_PATIENT_TAG:
      return {
        ...state,
        rooms: updateWhere(
          state.rooms,
          room => room.patientId === action.payload.patientId,
          room => {
            room.patient = removePatientTagFromPatient(room.patient, action.payload.tag)
            room.tagKeywords = room.tagKeywords.filter(tag => tag !== action.payload.tag)
          }
        )
      }
    case Actions.UNASSIGN_PRACTICE_MEMBER_FROM_PATIENT:
      return {
        ...state,
        rooms: updateWhere(
          state.rooms,
          room => room.patientId === action.payload.patientId,
          room => {
            const assigneeTag = room.patientTags.items.find(patientTag => patientTag.tag.type === 'assignee')
            if (assigneeTag) {
              room.patient = removePatientTagFromPatient(room.patient, assigneeTag.tag.value)
              room.tagKeywords = room.tagKeywords.filter(tag => tag !== assigneeTag.tag.value)
            }
          }
        )
      }
    case Actions.REMOVE_CHAT_ROOM:
      return {
        ...state,
        rooms: updateWhere(
          state.rooms,
          room => room.patientId === action.payload.patientId,
          room => room._deleted === true
        )
      }
    case Actions.COPY_MESSAGE:
      return {
        ...state,
        shouldAutoScrollChat: false
      }
    case Actions.DELETE_MESSAGE:
      return {
        ...state,
        shouldAutoScrollChat: false,
        messages: {
          ...state.messages,
          items: updateWhere(
            state.messages.items,
            message => message.id === action.payload.id,
            message => (message.isDeleting = true)
          )
        }
      }
    case Actions.UPDATE_TREATMENT_STATUS_RECEIVED:
      return {
        ...state,
        rooms: updateWhere(
          state.rooms,
          room => room.patientId === action.payload.patientId,
          room => (room.statusKey = action.payload.statusKey)
        )
      }
    case Actions.UPDATE_TREATMENT_RECEIVED:
      return {
        ...state,
        rooms: updateWhere(
          state.rooms,
          room => room.username === action.payload.treatment.a_patient,
          room => (room.statusKey = action.payload.treatment.currentStatus.statusKey)
        )
      }
    case Actions.DELETED_MESSAGE_RECEIVED:
      return {
        ...state,
        messages: {
          ...state.messages,
          items: replaceItem(state.messages.items, action.payload)
        }
      }
    case Actions.FETCH_STATIC_DATA_RECEIVED: {
      return {
        ...state,
        promotions: {
          ...state.promotions,
          data: mapToPromotions(action.payload.promotions),
          isFetching: false,
          fetchError: false
        }
      }
    }
    case Actions.SAVE_PROMOTION:
      return {
        ...state,
        promotions: {
          ...state.promotions,
          isSaving: true
        }
      }
    case Actions.SAVE_PROMOTION_RECEIVED: {
      return {
        ...state,
        promotions: {
          ...state.promotions,
          isSaving: false,
          data: upsertItem({ array: state.promotions.data, item: action.payload })
        }
      }
    }
    case Actions.SAVE_PROMOTION_FAILED: {
      return {
        ...state,
        promotions: {
          ...state.promotions,
          isSaving: false
        }
      }
    }
    case Actions.DELETE_PROMOTION: {
      return {
        ...state,
        promotions: {
          ...state.promotions,
          isSaving: true
        }
      }
    }
    case Actions.DELETE_PROMOTION_RECEIVED: {
      return {
        ...state,
        promotions: {
          ...state.promotions,
          isSaving: false,
          data: updateWhere(
            state.promotions.data,
            promotion => promotion.id === action.payload.id,
            promotion => {
              promotion.categoryKey = action.payload.categoryKey
              promotion._version = action.payload._version
            }
          )
        }
      }
    }
    case Actions.DELETE_PROMOTION_FAILED: {
      return {
        ...state,
        promotions: {
          ...state.promotions,
          isSaving: false
        }
      }
    }
    case Actions.LAST_PROMOTION_MESSAGES_RECEIVED: {
      return {
        ...state,
        promotions: {
          ...state.promotions,
          lastMessages: {
            ...state.promotions.lastMessages,
            roomId: action.payload.roomId,
            data: action.payload.messages
          }
        }
      }
    }
    case Actions.TOGGLE_BROADCAST_MODE: {
      return {
        ...state,
        broadcast: {
          ...initialState.broadcast,
          isModeOn: action.payload
        }
      }
    }
    case Actions.TOGGLE_BROADCAST_PERSONAL_MESSAGE: {
      return {
        ...state,
        broadcast: {
          ...state.broadcast,
          isPersonalMessage: action.payload
        }
      }
    }
    case Actions.UNSELECT_PATIENT_FOR_BROADCAST: {
      if (state.broadcast.allSelected) {
        return {
          ...state,
          broadcast: {
            ...state.broadcast,
            excluded: [...state.broadcast.excluded, action.payload],
            localMessages: initialState.broadcast.localMessages
          }
        }
      } else {
        return {
          ...state,
          broadcast: {
            ...state.broadcast,
            selectedPatients: removeWhere(state.broadcast.selectedPatients, id => id === action.payload),
            localMessages: initialState.broadcast.localMessages
          }
        }
      }
    }
    case Actions.SELECT_PATIENT_FOR_BROADCAST: {
      if (state.broadcast.allSelected) {
        return {
          ...state,
          broadcast: {
            ...state.broadcast,
            excluded: removeWhere(state.broadcast.excluded, id => id === action.payload),
            localMessages: initialState.broadcast.localMessages
          }
        }
      } else {
        return {
          ...state,
          broadcast: {
            ...state.broadcast,
            selectedPatients: [...state.broadcast.selectedPatients, action.payload],
            localMessages: initialState.broadcast.localMessages
          }
        }
      }
    }
    case Actions.TOGGLE_BROADCAST_SELECT_ALL: {
      if (action.payload) {
        return {
          ...state,
          broadcast: {
            ...state.broadcast,
            allSelected: true,
            selectedPatients: initialState.broadcast.selectedPatients,
            localMessages: initialState.broadcast.localMessages
          }
        }
      } else {
        return {
          ...state,
          broadcast: {
            ...state.broadcast,
            allSelected: false,
            excluded: initialState.broadcast.excluded,
            localMessages: initialState.broadcast.localMessages
          }
        }
      }
    }
    case Actions.TOGGLE_BROADCAST_MODAL: {
      return {
        ...state,
        broadcast: {
          ...state.broadcast,
          isConfirmationModalOpen: action.payload
        }
      }
    }
    case Actions.CREATE_BROADCAST_MESSAGE:
    case Actions.CREATE_BROADCAST_FILE_MESSAGE: {
      return {
        ...state,
        broadcast: {
          ...state.broadcast,
          isLoading: true
        }
      }
    }
    case Actions.CREATE_BROADCAST_MESSAGE_RECEIVED:
    case Actions.CREATE_BROADCAST_MESSAGE_FAILED:
    case Actions.CREATE_BROADCAST_FILE_MESSAGE_FAILED: {
      return {
        ...state,
        broadcast: {
          ...state.broadcast,
          isLoading: false,
          isConfirmationModalOpen: false
        }
      }
    }
    case Actions.RESET_BROADCAST_STATE: {
      return {
        ...state,
        broadcast: {
          ...initialState.broadcast,
          isModeOn: true
        }
      }
    }
    case Actions.CREATE_BROADCAST_LOCAL_MESSAGE: {
      return {
        ...state,
        broadcast: {
          ...state.broadcast,
          localMessages: upsertMessagesList(state.broadcast.localMessages, action.payload, false)
        }
      }
    }
    case Actions.DELETE_SCHEDULED_MESSAGE: {
      return {
        ...state,
        scheduledMessages: {
          ...state.scheduledMessages,
          items: updateWhere(
            state.scheduledMessages.items,
            scheduledMessage => scheduledMessage.id === action.payload.id,
            scheduledMessage => (scheduledMessage.isDeleting = true)
          )
        }
      }
    }
    case Actions.DELETE_SCHEDULED_MESSAGE_FAILED: {
      return {
        ...state,
        scheduledMessages: {
          ...state.scheduledMessages,
          items: updateWhere(
            state.scheduledMessages.items,
            scheduledMessage => scheduledMessage.id === action.payload.id,
            scheduledMessage => (scheduledMessage.isDeleting = false)
          )
        }
      }
    }
    case Actions.DELETE_SCHEDULED_MESSAGE_RECEIVED: {
      return {
        ...state,
        scheduledMessages: {
          ...state.scheduledMessages,
          items: removeWhere(
            state.scheduledMessages.items,
            scheduledMessage => scheduledMessage.id === action.payload.id
          )
        }
      }
    }
    case Actions.SEND_CHAT_MESSAGE: {
      return {
        ...state,
        scheduledMessages: {
          ...state.scheduledMessages,
          wasLastSentMessageScheduled: !!action.payload.scheduleTime
        }
      }
    }
    case Actions.MARK_ROOM_READ: {
      return {
        ...state,
        rooms: updateWhere(
          state.rooms,
          room => room.roomId === action.payload.roomId,
          room => (room.isChatRoomResolved = true)
        )
      }
    }
    case Actions.MARK_ROOM_READ_FAILED: {
      return {
        ...state,
        rooms: updateWhere(
          state.rooms,
          room => room.roomId === action.payload.roomId,
          room => (room.isChatRoomResolved = false)
        )
      }
    }
    case Actions.RESOLVE_SCAN_URGENCY:
      const { messageId } = action.payload

      return {
        ...state,
        messages: {
          ...state.messages,
          items: updateWhere(
            state.messages.items,
            message => message.id === messageId,
            message => (message.metadata = JSON.stringify({ ...JSON.parse(message.metadata || '{}'), isUrgent: false }))
          )
        }
      }
    case Actions.SOFT_DELETE_CHAT_MESSAGE:
    case Actions.SOFT_DELETE_CHAT_MESSAGE_RECEIVED:
      return {
        ...state,
        messages: {
          ...state.messages,
          items: updateWhere(
            state.messages.items,
            msg => msg.id === action.payload.messageId,
            msg => {
              msg.isDeleted = true
              msg.lastEditedAt = new Date().toISOString()
            }
          )
        }
      }
    case Actions.SOFT_DELETE_CHAT_MESSAGE_FAILED:
      return {
        ...state,
        messages: {
          ...state.messages,
          items: updateWhere(
            state.messages.items,
            msg => msg.id === action.payload.messageId,
            msg => {
              msg.isDeleted = false
            }
          )
        }
      }
    case Actions.SUBMIT_SCAN_TRACKING_DATA_RECEIVED:
      return {
        ...state,
        messages: {
          ...state.messages,
          items: updateWhere(
            state.messages.items,
            msg => msg.entityRelatedId === action.payload.grinScan.id,
            msg => (msg.grinScan = { ...msg.grinScan, ...action.payload.grinScan })
          )
        }
      }
    case Actions.EDIT_CHAT_MESSAGE:
      return {
        ...state,
        messageActions: {
          ...state.messageActions,
          editMessage: {
            ...state.messageActions.editMessage,
            loadStatus: AsyncStatus.Loading
          }
        }
      }
    case Actions.EDIT_CHAT_MESSAGE_FAILED:
      return {
        ...state,
        messageActions: {
          ...state.messageActions,
          editMessage: {
            ...state.messageActions.editMessage,
            loadStatus: AsyncStatus.Failed
          }
        }
      }
    case Actions.EDIT_CHAT_MESSAGE_RECEIVED:
      return {
        ...state,
        messages: {
          ...state.messages,
          items:
            action.payload.updatedMessage.roomId !== state.activeRoomId
              ? state.messages.items
              : updateAllWhere(
                  state.messages.items,
                  msg => msg.id === action.payload.updatedMessage.id,
                  msg => ({
                    ...msg,
                    ...action.payload.updatedMessage
                  })
                )
        },
        messageActions: {
          ...state.messageActions,
          editMessage: {
            ...state.messageActions.editMessage,
            loadStatus: AsyncStatus.Completed
          }
        }
      }
    case Actions.FETCH_MESSAGE_AUDIT:
      return {
        ...state,
        messageActions: {
          ...state.messageActions,
          viewHistory: {
            ...state.messageActions.viewHistory,
            loadStatus: AsyncStatus.Loading
          }
        }
      }
    case Actions.FETCH_MESSAGE_AUDIT_FAILED:
      return {
        ...state,
        messageActions: {
          ...state.messageActions,
          viewHistory: {
            ...state.messageActions.viewHistory,
            loadStatus: AsyncStatus.Failed
          }
        }
      }
    case Actions.FETCH_MESSAGE_AUDIT_RECEIVED:
      return {
        ...state,
        messageActions: {
          ...state.messageActions,
          viewHistory: {
            ...state.messageActions.viewHistory,
            loadStatus: AsyncStatus.Completed,
            message: action.payload.message
          }
        }
      }
    case Actions.REQUEST_SEND_SCAN_REVIEW:
      // This is only relevant when retrying to upload the scan review
      return {
        ...state,
        messages: {
          ...state.messages,
          items: updateWhere(
            state.messages.items,
            msg => msg.s3UniqueId === action.payload.s3UniqueId,
            msg => {
              msg.uploadStatus = AsyncStatus.Loading
            }
          )
        }
      }
    case Actions.SEND_SCAN_REVIEW_RECEIVED:
      return {
        ...state,
        messages: {
          ...state.messages,
          items: updateWhere(
            state.messages.items,
            msg => msg.s3UniqueId === action.payload.s3UniqueId,
            msg => {
              msg.uploadStatus = AsyncStatus.Completed
            }
          )
        },
        uploadingMessages: state.uploadingMessages.filter(
          uploadingMessage => uploadingMessage.s3UniqueId !== action.payload.s3UniqueId
        )
      }
    case Actions.SEND_SCAN_REVIEW_FAILED:
      return {
        ...state,
        messages: {
          ...state.messages,
          items: updateWhere(
            state.messages.items,
            msg => msg.s3UniqueId === action.payload.s3UniqueId,
            msg => {
              msg.uploadStatus = AsyncStatus.Failed
            }
          )
        }
      }
    case Actions.CHANGE_PATIENTS_SORT_DIRECTION:
      return {
        ...state,
        sort: {
          ...state.sort,
          direction: action.payload || SortDirections.Desc
        }
      }
    case Actions.REACT_TO_MESSAGE: {
      const { messageId, reaction, grinUserId } = action.payload
      return {
        ...state,
        messages: {
          ...state.messages,
          items: updateWhere(
            state.messages.items,
            msg => msg.id === messageId,
            msg => {
              const parsedReactions = JSON.parse(msg.reactions || '{}')
              let updatedReactions

              if (parsedReactions[grinUserId]?.reaction === reaction) {
                delete parsedReactions[grinUserId]
                updatedReactions = { ...parsedReactions }
              } else {
                updatedReactions = {
                  ...parsedReactions,
                  [grinUserId]: {
                    ...parsedReactions?.[grinUserId],
                    reaction
                  }
                }
              }

              msg.reactions = JSON.stringify(updatedReactions)
            }
          )
        }
      }
    }
    default:
      return state
  }
}
